import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Briefcase, Rocket, Check, ArrowRight, ArrowLeft, RotateCcw } from "lucide-react";
import { scrollToId } from "@/pages/LandingPage";

const EASE = [0.22, 1, 0.36, 1];

const PATHS = [
  { id: "family", icon: Users, title: "Protect my family", desc: "Wills, tickets, everyday legal questions" },
  { id: "business", icon: Briefcase, title: "Protect my business", desc: "Contracts, collections, HR questions" },
  { id: "partner", icon: Rocket, title: "Build my own business", desc: "Share these plans, earn commissions" },
];

const INTEREST_MAP = { family: "individual", business: "business", partner: "partner" };

const QUESTIONS = {
  family: {
    q: "Who needs coverage?",
    options: [
      { id: "couple", label: "Just me — or me and my spouse", plan: "Basic Plan", price: "39.95", why: ["Covers you and your spouse", "Will, power of attorney & unlimited advice", "24/7 emergency access"] },
      { id: "kids", label: "My whole household, kids included", plan: "Advanced Plan", price: "49.95", why: ["Adds eligible dependent children", "Annual will updates included", "Uncontested divorce, adoption & prenup help"] },
      { id: "estate", label: "I need trust & estate help", plan: "Premium Plan", price: "59.95", why: ["Revocable trust preparation", "IRS audit representation", "25% off services beyond the plan"] },
    ],
  },
  business: {
    q: "How big is your team?",
    options: [
      { id: "solo", label: "It's just me", plan: "Small Business Plan", price: null, why: ["Contract review before you sign", "Debt collection letters that get answered", "Priced for solo owners — John will quote it"] },
      { id: "team", label: "I have employees", plan: "Small Business Plan — Team", price: null, why: ["HR, hiring & termination guidance", "Higher document review volume", "Tiered by headcount — John will quote it"] },
    ],
  },
  partner: {
    q: "How much time can you give this each week?",
    options: [
      { id: "parttime", label: "A few evenings, around my job", plan: "Partner Track — Part-time Start", price: null, why: ["Most partners begin alongside a day job", "Step-by-step training and mentorship", "Straight talk: results track your effort — no income promises"] },
      { id: "fulltime", label: "I'm ready to go all in", plan: "Partner Track — Full Focus", price: null, why: ["Faster ramp with consistent daily activity", "Same training, bigger pipeline", "Straight talk: results track your effort — no income promises"] },
    ],
  },
};

export default function PathFinder() {
  const [path, setPath] = useState(null);
  const [answer, setAnswer] = useState(null);

  useEffect(() => {
    const onSelect = (e) => {
      setPath(e.detail);
      setAnswer(null);
    };
    window.addEventListener("path-select", onSelect);
    return () => window.removeEventListener("path-select", onSelect);
  }, []);

  const step = !path ? 1 : !answer ? 2 : 3;
  const question = path ? QUESTIONS[path] : null;
  const chosen = answer ? question.options.find((o) => o.id === answer) : null;

  const goToForm = () => {
    window.dispatchEvent(new CustomEvent("lead-interest", { detail: INTEREST_MAP[path] }));
    window.dispatchEvent(new CustomEvent("lead-message", { detail: `Guided path recommendation: ${chosen.plan}` }));
    scrollToId("#get-started");
  };

  const restart = () => {
    setPath(null);
    setAnswer(null);
  };

  return (
    <section id="your-path" className="bg-white py-16 md:py-32 scroll-mt-20" data-testid="pathfinder-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: EASE }}
          className="font-mono-accent text-xs uppercase tracking-[0.28em] text-blue-600 mb-5 text-center"
        >
          Start here
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ delay: 0.1, duration: 0.7, ease: EASE }}
          className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B192C] text-center leading-[1.1] mb-4"
        >
          Find your path in 30 seconds.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.7 }}
          className="text-slate-600 text-center max-w-xl mx-auto mb-6 sm:mb-10"
        >
          Two quick questions, one honest recommendation. No email needed until you ask for the details.
        </motion.p>

        <div className="flex items-center justify-center gap-2 mb-6 sm:mb-10" data-testid="pathfinder-steps">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold font-mono-accent transition-colors duration-300 ${
                step >= n ? "bg-[#1D4ED8] text-white" : "bg-slate-100 text-slate-400"
              }`}>
                {n}
              </span>
              {n < 3 && <span className={`w-10 h-0.5 rounded ${step > n ? "bg-[#1D4ED8]" : "bg-slate-200"} transition-colors duration-300`} />}
            </div>
          ))}
        </div>

        <div className="min-h-[340px]">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4, ease: EASE }}
                className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                {PATHS.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`path-tile-${p.id}`}
                    onClick={() => {
                      setPath(p.id);
                      if (!sessionStorage.getItem("mll_path_started")) {
                        sessionStorage.setItem("mll_path_started", "1");
                        window.__track?.("path_start");
                      }
                    }}
                    className="group w-full text-left rounded-2xl sm:rounded-3xl border border-slate-200 bg-[#F8FAFC] p-4 sm:p-7 flex sm:block items-center gap-4 transition-all duration-300 hover:-translate-y-1.5 hover:border-blue-400 hover:shadow-xl hover:shadow-blue-100"
                  >
                    <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-[#EFF6FF] border border-blue-200 text-blue-700 flex items-center justify-center mb-0 sm:mb-5 shrink-0 transition-colors duration-300 group-hover:bg-[#1D4ED8] group-hover:text-white group-hover:border-[#1D4ED8]">
                      <p.icon size={20} strokeWidth={1.8} />
                    </span>
                    <div className="flex-1">
                      <p className="font-display font-bold text-base sm:text-lg text-[#0B192C] mb-0.5 sm:mb-1.5">{p.title}</p>
                      <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">{p.desc}</p>
                    </div>
                    <ArrowRight size={16} className="text-slate-300 group-hover:text-blue-600 transition-colors duration-300 shrink-0 sm:hidden" />
                  </button>
                ))}
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4, ease: EASE }}>
                <p className="font-display font-bold text-2xl text-[#0B192C] text-center mb-8" data-testid="pathfinder-question">{question.q}</p>
                <div className={`grid grid-cols-1 ${question.options.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2 max-w-2xl mx-auto"} gap-4`}>
                  {question.options.map((o) => (
                    <button
                      key={o.id}
                      data-testid={`path-option-${o.id}`}
                      onClick={() => setAnswer(o.id)}
                      className="rounded-2xl border border-slate-200 bg-white px-6 py-5 text-center font-medium text-slate-700 transition-all duration-300 hover:-translate-y-1 hover:border-blue-500 hover:text-blue-700 hover:shadow-lg hover:shadow-blue-100"
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <div className="text-center mt-8">
                  <button data-testid="path-back-button" onClick={() => setPath(null)} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-blue-700 transition-colors">
                    <ArrowLeft size={14} /> Back
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && chosen && (
              <motion.div key="step3" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5, ease: EASE }}
                className="rounded-[2rem] bg-[#0B192C] grain-overlay relative overflow-hidden p-8 md:p-12" data-testid="path-result">
                <div className="absolute inset-0 hero-spotlight" />
                <div className="relative">
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-sky-400 mb-3">Your recommended path</p>
                  <h3 className="font-display font-extrabold text-3xl sm:text-4xl text-white tracking-tight mb-2">
                    {chosen.plan}
                    {chosen.price && <span className="text-sky-400"> — ${chosen.price}/mo</span>}
                  </h3>
                  <ul className="space-y-3 my-8">
                    {chosen.why.map((w) => (
                      <li key={w} className="flex items-start gap-3 text-slate-200">
                        <Check size={17} className="text-sky-400 mt-0.5 shrink-0" />
                        {w}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <button
                      data-testid="path-cta-button"
                      onClick={goToForm}
                      className="group flex items-center gap-2 bg-sky-400 hover:bg-sky-300 text-[#0B192C] font-semibold px-7 py-3.5 rounded-full transition-colors duration-300"
                    >
                      Get my plan details from John
                      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                    <button data-testid="path-restart-button" onClick={restart} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
                      <RotateCcw size={13} /> Start over
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-6">No obligation — you get the full coverage documents before you decide anything.</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
